import "../../common/elements/Header.scss";
import { useContext, useEffect, useReducer } from "react";
import { sendRequest } from "../../../common/http";
import translate from "@/i18n/translate";
import WidgetLoader from "../../common/elements/WidgetLoader";
import UnauthorizedComponent from "@/features/insights/UnauthorizedComponent";
import { WmButton } from "@watermarkinsights/ripple-react";
import { ShareAccessModal } from "./ShareAccessModal";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import {
  OrganizationWithAccess,
  People,
  Program
} from "@/features/common/interfaces/Interface";
import { Link } from "react-router-dom";
import { scrollToTop } from "@/features/common/helper";
import ApiError from "@/features/common/elements/ApiError";
import { usePageTitle } from '@/hooks/usePageTitle';
import { useIntl } from 'react-intl';

interface AcademicAndOfficeState {
  showPrograms: boolean;
  programs: Program[] | null;
  institution: Program | null;
  loadingStates: {
    programs: boolean;
    organizations: boolean;
    people: boolean;
  };
  showLoader: boolean;
  availablePeople: People[] | null;
  showShareAccessModal: boolean;
  modalData: ModalData;
  apiError: boolean;
  organizations: OrganizationWithAccess[] | null;
  showOrganizations: boolean;
}

export interface ModalData {
  title: string;
  nodeUuid: string;
  nodeType: string;
  selectedPeople: People[];
}

interface AcademicAndOfficeAction {
  type: string;
  programs: Program[] | null;
  institution: Program | null;
  showPrograms: boolean;
  availablePeople: People[] | null;
  modalData: ModalData;
  uuid: string;
  nodeType: string;
  peopleWithAccess: People[];
  apiError: boolean;
  organizations: OrganizationWithAccess[] | null;
  showOrganizations: boolean;
}
const defaultModalData: ModalData = {
  title: "",
  nodeUuid: "",
  nodeType: "",
  selectedPeople: []
};
const defaultAction: AcademicAndOfficeAction = {
  type: "",
  programs: [],
  showPrograms: true,
  institution: null,
  availablePeople: [],
  modalData: defaultModalData,
  uuid: "",
  nodeType: "",
  peopleWithAccess: [],
  apiError: false,
  organizations: null,
  showOrganizations: true
};

function updatePrograms(
  programs: Program[] | null,
  uuid: string,
  peopleWithAccess: People[]
): Program[] | null {
  return (
    programs?.map((progm) =>
      progm.uuid === uuid
        ? { ...progm, peopleWithAccess: peopleWithAccess }
        : progm
    ) ?? null
  );
}

function updateOrganizations(
  organizations: OrganizationWithAccess[] | null,
  uuid: string,
  peopleWithAccess: People[]
): OrganizationWithAccess[] | null {
  return (
    organizations?.map((org) =>
      org.uuid === uuid
        ? { ...org, peopleWithAccess: peopleWithAccess }
        : org
    ) ?? null
  );
}

function updateInstitution(
  institution: Program | null,
  peopleWithAccess: People[]
): Program | null {
  return institution
    ? { ...institution, peopleWithAccess: peopleWithAccess }
    : null;
}

function actionUpdatePeopleWithAccess(
  state: AcademicAndOfficeState,
  action: AcademicAndOfficeAction
) {
  if (action.nodeType === "program") {
    return {
      ...state,
      programs: updatePrograms(
        state.programs,
        action.uuid,
        action.peopleWithAccess
      )
    };
  }
  else if (
    action.nodeType === "College" ||
    action.nodeType === "School"
  ) {
    return {
      ...state,
      organizations: updateOrganizations(
        state.organizations,
        action.uuid,
        action.peopleWithAccess
      )
    };
  }
  else {
    return {
      ...state,
      institution: updateInstitution(state.institution, action.peopleWithAccess)
    };
  }
}

const reducer = (
  state: AcademicAndOfficeState,
  action: AcademicAndOfficeAction
) => {
  switch (action.type) {
    case "updatePeopleWithAccess":
      return actionUpdatePeopleWithAccess(state, action);

    case "setPrograms": {
      const newLoadingStates = { ...state.loadingStates, programs: false };
      return {
        ...state,
        programs: action.programs,
        institution: action.institution,
        loadingStates: newLoadingStates,
        showLoader: computeShowLoader(newLoadingStates)
      };
    }
    case "setOrganizations": {
      const newLoadingStates = { ...state.loadingStates, organizations: false };
      return {
        ...state,
        organizations: action.organizations,
        loadingStates: newLoadingStates,
        showLoader: computeShowLoader(newLoadingStates)
      };
    }
    case "showPrograms":
      return {
        ...state,
        showPrograms: !state.showPrograms
      };
    case "showOrganizations":
      return {
        ...state,
        showOrganizations: !state.showOrganizations
      };
    case "toggleShareAccessModal":
      return {
        ...state,
        showShareAccessModal: !state.showShareAccessModal
      };
    case "setPeople": {
      const newLoadingStates = { ...state.loadingStates, people: false };
      return {
        ...state,
        availablePeople: action.availablePeople,
        loadingStates: newLoadingStates,
        showLoader: computeShowLoader(newLoadingStates)
      };
    }
    case "setModalData":
      return {
        ...state,
        modalData: action.modalData
      };
    case "setApiError": {
      const newLoadingStates = {
        ...state.loadingStates,
        programs: false,
        organizations: false,
        people: false
      };
      return {
        ...state,
        apiError: true,
        loadingStates: newLoadingStates,
        showLoader: computeShowLoader(newLoadingStates)
      };
    }

    default:
      return state;
  }
};

export interface AcademicsAndOfficesProps {
  auth: boolean;
}

export default function AcademicsAndOffices({
  auth
}: AcademicsAndOfficesProps): JSX.Element {
  const initialState = {
    showPrograms: true,
    programs: null,
    institution: null,
    availablePeople: null,
    showShareAccessModal: false,
    modalData: defaultModalData,
    apiError: false,
    organizations: null,
    showOrganizations: true,
    loadingStates: {
      programs: true,
      organizations: true,
      people: true
    },
    showLoader: true
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const intl = useIntl();

  // Add this line to set the page title
  const pageTitle = intl.formatMessage({ id: 'app.pageTitle.academicsAndOffices' });
  usePageTitle(pageTitle);

  useEffect(() => {
    const applyResult = (result: {
      institution: Program;
      programs: Program[];
    }) => {
      dispatch({
        ...defaultAction,
        type: "setPrograms",
        programs: result.programs,
        institution: result.institution
      });
    };
    const onError = () => {
      dispatch({
        ...defaultAction,
        type: "setApiError"
      });
    };

    auth &&
      sendRequest(
        { uri: "/sa/programs", camelize: true },
        applyResult,
        onError
      );
  }, [auth]);

  useEffect(() => {
    const applyResult = (result: {
      institution: OrganizationWithAccess;
      organizations: OrganizationWithAccess[];
    }) => {
      dispatch({
        ...defaultAction,
        type: "setOrganizations",
        organizations: result.organizations,
        institution: result.institution
      });
    };
    const onError = () => {
      dispatch({
        ...defaultAction,
        type: "setApiError"
      });
    };

    auth &&
      sendRequest(
        { uri: "/sa/organizations", camelize: true },
        applyResult,
        onError
      );
  }, [auth]);

  useEffect(() => {
    const applyResult = (result: People[]) => {
      dispatch({
        ...defaultAction,
        type: "setPeople",
        availablePeople: result
      });
    };
    const onError = () => {
      dispatch({
        ...defaultAction,
        type: "setApiError"
      });
    };

    auth &&
      sendRequest({ uri: "/sa/people", camelize: true }, applyResult, onError);
  }, [auth]);

  function expandTable() {
    dispatch({ ...defaultAction, type: "showPrograms" });
    dispatch({ ...defaultAction, type: "showOrganizations" });
  }
  function updatePeopleWithAccess(
    uuids: Set<string>,
    type: string,
    nodeUuid: string
  ) {
    let peopleWithAccess: People[] = new Array(0);
    uuids.forEach((uuid: string) => {
      let person = state.availablePeople?.find(
        (person: People) => person.uuid === uuid
      );

      person && peopleWithAccess.push(person);
    });

    dispatch({
      ...defaultAction,
      type: "updatePeopleWithAccess",
      uuid: nodeUuid,
      nodeType: type,
      peopleWithAccess: peopleWithAccess
    });
  }

  function toggleShareAccessModal(modalData?: ModalData) {
    dispatch({ ...defaultAction, type: "toggleShareAccessModal" });
    if (modalData) {
      dispatch({
        ...defaultAction,
        type: "setModalData",
        modalData: modalData
      });
    }
  }



  return (
    <>
      {auth ? (
        <>
          <div className="page-header">
            <div className="title-container">
              <h2 id="main-heading" className="title zero-top-margin">
                {translate("app.mainnav.academicsAndOffices")}
              </h2>
              <div className="title-description">
                {translate("app.home.org.desc")}
              </div>
            </div>
          </div>
          {state.showLoader ? (
            <WidgetLoader
              loaderContainerClasses="loader"
              loaderClasses="content"
            />
          ) : (
            <>
              {state.apiError && <ApiError containerClasses="api-error" />}

              {!state.apiError && (
                <>
                  <div
                    className="org-node paper-card -nopadding org-management"
                    role="grid"
                    aria-readonly="true"
                  >
                    <div className="orgnodeheader grid-row" role="row">
                      <div className="column" role="columnheader">
                        {translate("app.drilldown.organization")} 
                      </div>
                      <div className="column" role="columnheader">
                        {translate("app.academicsAndOffices.peopleWithAccess")}
                      </div>
                    </div>
                    {state.institution && (
                      <InstitutionRow
                        institution={state.institution}
                        showPrograms={state.showPrograms}
                        showOrganizations={state.showOrganizations}
                        expandTable={expandTable}
                        toggleShareAccessModal={toggleShareAccessModal}
                      />
                    )}
                    <div
                      id="org-nodes"
                      className={
                        state.showPrograms && state.showOrganizations
                          ? ""
                          : "expand-node"
                      }
                    >
                      {state.programs &&
                        state.programs.map((program: Program, index: number) => {
                          return (
                            <ProgramRow
                              program={program}
                              showPrograms={state.showPrograms}
                              key={`${program.uuid} ${index}  ${program.code}`}
                              toggleShareAccessModal={toggleShareAccessModal}
                            />
                          );
                        })
                      }
                      {state.organizations &&
                        state.organizations.map((organization: OrganizationWithAccess, index: number) => {
                          return (
                            <OrganizationRow
                              organization={organization}
                              showOrganizations={state.showOrganizations}
                              key={`${organization.uuid} ${index}  ${organization.code}`}
                              toggleShareAccessModal={toggleShareAccessModal}
                            />
                          );
                        })
                      }
                    </div>
                  </div>
                  <ShareAccessModal
                    toggleShareAccessModal={toggleShareAccessModal}
                    updatePeopleWithAccess={updatePeopleWithAccess}
                    showShareAccessModal={state.showShareAccessModal}
                    availablePeople={state.availablePeople}
                    modalData={state.modalData}
                    key={JSON.stringify(state.modalData)}
                  />
                </>
              )}
            </>
          )}
        </>
      ) : (
        <UnauthorizedComponent />
      )}
    </>
  );
}
const queryString = window.location.search;
interface ProgramRowProps {
  program: Program;
  showPrograms: boolean;
  toggleShareAccessModal: (modalData?: ModalData) => void;
}
function ProgramRow({
  program,
  showPrograms,
  toggleShareAccessModal
}: ProgramRowProps) {
  const { institution: ctxInstitution, setBreadcrumb } =
    useContext<AppContextInterface>(AppContext);
  return (
    <div className="orgnode grid-row hierarchy-level" role="row">
      <div className="column" role="gridcell">
        <div className="focus-space">
          <div className="treeitemlabel">
            <Link
              to={
                queryString.includes("locale")
                  ? `/insights/institution/${ctxInstitution?.platformUuid}/program/${program.uuid}/${queryString}`
                  : `/insights/institution/${ctxInstitution?.platformUuid}/program/${program.uuid}`
              }
              id="toolsNavItemLink"
              className="hierarchy-link"
              onClick={() => {
                scrollToTop(".page-header");
                setBreadcrumb("academic-office");
              }}
            >
              {program.name}
            </Link>
          </div>
          <p className="node-info">
            <span className="infoheader">
              {translate("app.home.org.type")}
              <span className="infodesc">
                {translate("app.strategy.topProgramsByCatalog.program")}
              </span>
            </span>
          </p>
          <p className="node-info">
            <span className="infoheader">
              {translate("app.home.org.code")}
              <span className="infodesc">{program.code}</span>
            </span>
          </p>
        </div>
      </div>
      <div className="column cell" role="gridcell">
        <ShareAccessButton
          node={program}
          toggleShareAccessModal={toggleShareAccessModal}
          nodeType="program"
        />
      </div>
    </div>
  );
}

interface InstitutionRowProps {
  institution: Program | null;
  showPrograms: boolean;
  showOrganizations: boolean;
  expandTable: () => void;
  toggleShareAccessModal: (modalData?: ModalData) => void;
}
function InstitutionRow({
  institution,
  showPrograms,
  showOrganizations,
  expandTable,
  toggleShareAccessModal
}: InstitutionRowProps) {
  const { institution: ctxInstitution, setBreadcrumb } =
    useContext<AppContextInterface>(AppContext);

  return (
    <div className="orgnode grid-row" role="row">
      <div className="column" role="gridcell">
        <button
          aria-expanded={showPrograms && showOrganizations}
          aria-controls="org-nodes"
          className={
            showPrograms && showOrganizations
              ? "expand-button mdi  mdi-menu-down"
              : "expand-button mdi  mdi-menu-right"
          }
          aria-label="Toggle tree view of University"
          onClick={expandTable}
        ></button>
        <div className="focus-space">
          <div className="treeitemlabel program-name">
            <Link
              to={
                queryString.includes("locale")
                  ? `/insights/institution/${ctxInstitution?.platformUuid}/${queryString}`
                  : `/insights/institution/${ctxInstitution?.platformUuid}`
              }
              id="toolsNavItemLink"
              className="hierarchy-link"
              onClick={() => {
                scrollToTop(".page-header");
                setBreadcrumb("academic-office");
              }}
            >
              {institution?.name}
            </Link>
          </div>
          <p className="node-info">
            <span className="infoheader">
              {translate("app.home.org.type")}
              <span className="infodesc">
                {translate("app.home.org.institution")}
              </span>
            </span>
          </p>
          <p className="node-info">
            <span className="infoheader">
              {translate("app.home.org.code")}
              <span className="infodesc">{institution?.code}</span>
            </span>
          </p>
        </div>
      </div>
      <div className="column cell" role="gridcell">
        <ShareAccessButton
          node={institution}
          toggleShareAccessModal={toggleShareAccessModal}
          nodeType="institution"
        />
      </div>
    </div>
  );
}

interface ShareAccessButtonProps {
  node: Program | null;
  toggleShareAccessModal: (modalData?: ModalData) => void;
  nodeType: string;
}

function ShareAccessButton({
  node,
  toggleShareAccessModal,
  nodeType
}: ShareAccessButtonProps) {
  const intl = useIntl();

  const getAriaLabel = (type: string, nodeName: string) => {
    let typeFormatted: string;

    switch (type?.toLowerCase()) {
      case "program":
        typeFormatted = intl.formatMessage({
          id: "app.strategy.topProgramsByCatalog.program"
        });
        break;
      case "institution":
        typeFormatted = intl.formatMessage({ id: "app.home.org.institution" });
        break;
      case "college":
        typeFormatted = intl.formatMessage({
          id: "app.home.org.college"
        });
        break;
      case "school":
        typeFormatted = intl.formatMessage({
          id: "app.home.org.school"
        });
        break;

      default:
        typeFormatted = type || "";
    }

    const nodeTypeText = (typeFormatted || "").toLowerCase();
    const baseLabel = `Share access to ${nodeName}${
      nodeTypeText ? ` ${nodeTypeText}` : ""
    }`;

    return baseLabel;
  };

  if (!node) {
    return null;
  }

  const baseAccessibleLabel = getAriaLabel(nodeType, node.name);
  const hasPeopleWithAccess =
    Array.isArray(node.peopleWithAccess) && node.peopleWithAccess.length > 0;
  const { limitedNames: peopleNamesForLabel, additionalCount } =
    getUniqueFormattedNames(
      hasPeopleWithAccess ? node.peopleWithAccess : undefined,
      MAX_DISPLAYED_NAMES
    );
  const peopleNamesLabel = [
    ...peopleNamesForLabel,
    ...(additionalCount > 0 ? [`+${additionalCount} more`] : [])
  ]
    .filter(Boolean)
    .join(", ");
  const accessibleLabel = peopleNamesLabel
    ? `${peopleNamesLabel}, ${baseAccessibleLabel}`
    : baseAccessibleLabel;

  const handleOnClick = () =>
    toggleShareAccessModal({
      ...defaultModalData,
      title: node.name,
      nodeType,
      nodeUuid: node.uuid,
      selectedPeople: hasPeopleWithAccess ? node.peopleWithAccess || [] : []
    });

  return (
    <WmButton
      button-type="textonly"
      class="-textonly hydrated"
      icon={hasPeopleWithAccess ? undefined : "f014"}
      labelForIdenticalButtons={accessibleLabel}
      onClick={handleOnClick}
    >
      {hasPeopleWithAccess ? (
        <span>
          <PeopleNames node={node} />
        </span>
      ) : (
        <span aria-hidden="true">
          {translate("app.academicsAndOffices.shareAccess")}
        </span>
      )}
    </WmButton>
  );
}

const MAX_DISPLAYED_NAMES = 5;

const formatPersonName = (person: People): string =>
  [person.givenName, person.middleName, person.familyName]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

const getUniqueFormattedNames = (
  people?: People[] | null,
  limit = MAX_DISPLAYED_NAMES
): { limitedNames: string[]; additionalCount: number } => {
  if (!people || people.length === 0) {
    return { limitedNames: [], additionalCount: 0 };
  }

  const uniqueNames = Array.from(
    new Set(people.map((person) => formatPersonName(person)).filter(Boolean))
  );

  const limitedNames = uniqueNames.slice(0, limit);

  return {
    limitedNames,
    additionalCount: Math.max(uniqueNames.length - limitedNames.length, 0)
  };
};

function PeopleNames({ node }: { node: Program }) {
  if (!node.peopleWithAccess) {
    return null;
  }

  const { limitedNames, additionalCount } = getUniqueFormattedNames(
    node.peopleWithAccess,
    MAX_DISPLAYED_NAMES
  );

  if (!limitedNames.length && additionalCount === 0) {
    return null;
  }

  return (
    <>
      {limitedNames.join(", ")}
      {additionalCount > 0 && ` +${additionalCount} more`}
    </>
  );
}

interface OrganizationRowProps {
  organization: OrganizationWithAccess;
  showOrganizations: boolean;
  toggleShareAccessModal: (modalData?: ModalData) => void;
}
function OrganizationRow({
  organization,
  showOrganizations,
  toggleShareAccessModal
}: OrganizationRowProps) {
  const { institution: ctxInstitution, setBreadcrumb } =
    useContext<AppContextInterface>(AppContext);
  return (
    <div className="orgnode grid-row hierarchy-level" role="row">
      <div className="column" role="gridcell">
        <div className="focus-space">
          <div className="treeitemlabel">
            <Link
              to={
                queryString.includes("locale")
                  ? `/insights/institution/${ctxInstitution?.platformUuid}/organization/${organization.uuid}/${queryString}`
                  : `/insights/institution/${ctxInstitution?.platformUuid}/organization/${organization.uuid}`
              }
              id="toolsNavItemLink"
              className="hierarchy-link"
              onClick={() => {
                scrollToTop(".page-header");
                setBreadcrumb("academic-office");
              }}
            >
              {organization.name}
            </Link>
          </div>
          <p className="node-info">
            <span className="infoheader">
              {translate("app.home.org.type")}
              <span className="infodesc">{organization.type}</span>
            </span>
          </p>
          <p className="node-info">
            <span className="infoheader">
              {translate("app.home.org.code")}
              <span className="infodesc">{organization.code}</span>
            </span>
          </p>
        </div>
      </div>
      <div className="column cell" role="gridcell">
        <ShareAccessButton
          node={organization}
          toggleShareAccessModal={toggleShareAccessModal}
          nodeType={organization.type}
        />
      </div>
    </div>
  );
}

function computeShowLoader(loadingStates: { programs: boolean; organizations: boolean; people: boolean }): boolean {
  return loadingStates.programs || loadingStates.organizations || loadingStates.people;
}
